/**
 * CustomerGuard: require valid user + company_id, reject admins.
 * Allows Super-Admin impersonation sessions (token in sessionStorage).
 * Unauthorized Cognito users see a warning and are signed out after 10 seconds.
 */
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { hasImpersonationSession } from "../impersonationSession";

const ADMIN_GROUPS = ["GlobalAdmin", ".AdminPortalUsers", ".AdminGroupBusinessInsightsViewers",
  ".AdminPortalSupportUsers", ".AdminPortalCustomerProvisioningAdmins",
  ".AdminPortalUserProvisioningAdmins", ".AdminPortalSuperAdmins"];

const SIGN_OUT_DELAY_MS = 10_000;

function normalizeGroups(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.flatMap((g) =>
      typeof g === "string" && g.startsWith("[")
        ? g.replace(/[\[\]]/g, "").split(/\s+/)
        : [g]
    );
  }
  if (typeof raw === "string") {
    return raw.replace(/[\[\]]/g, "").split(/\s+/).filter(Boolean);
  }
  return [];
}

export default function CustomerGuard({ user, signOut }) {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let alive = true;

    async function run() {
      try {
        if (hasImpersonationSession()) {
          if (alive) {
            setAllowed(true);
            setLoading(false);
          }
          return;
        }

        if (!user) {
          if (alive) {
            setAllowed(false);
            setLoading(false);
          }
          return;
        }

        const session = await fetchAuthSession();
        const payload = session?.tokens?.idToken?.payload || {};

        const companyId = payload["custom:companyId"] || payload["family_name"];
        const groups = normalizeGroups(payload["cognito:groups"]);
        const hasAdminGroup = groups.some((g) => ADMIN_GROUPS.includes(g));

        // Reject admins; require company_id for customers
        const ok = !hasAdminGroup && (companyId || "").trim().length > 0;

        if (alive) {
          setAllowed(ok);
          setLoading(false);
        }
      } catch (e) {
        console.error("CustomerGuard auth check failed:", e);
        if (alive) {
          setAllowed(false);
          setLoading(false);
        }
      }
    }

    run();
    return () => { alive = false; };
  }, [user]);

  // Match admin website: no cleanup - avoid clearing timer in React Strict Mode
  useEffect(() => {
    if (hasImpersonationSession()) return;
    if (!loading && !allowed && signOut && !signingOut) {
      setSigningOut(true);
      setTimeout(() => {
        signOut();
      }, SIGN_OUT_DELAY_MS);
    }
  }, [loading, allowed, signOut, signingOut]);

  if (loading) {
    return <div style={{ padding: 20 }}>Checking access…</div>;
  }

  if (!allowed) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#991b1b" }}>
        <h2>Access Denied</h2>
        <p style={{ marginTop: 12, fontSize: 16 }}>
          This portal is for customer users only. Administrators should use the admin portal.
        </p>
        <p style={{ marginTop: 8, color: "#6b7280", fontSize: 14 }}>
          If you believe you should have access, please contact your administrator.
        </p>
        <p style={{ marginTop: 16, color: "#6b7280", fontSize: 14 }}>
          You will be signed out in 10 seconds.
        </p>
      </div>
    );
  }

  return <Outlet />;
}
